import { useEffect, useState } from 'react';
import Scrollbars from '@/components/Scrollbars';
import { Dialog, DialogContent, dialogClasses } from '@/components/Dialog';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { formatPrice, hasPassedDays, setCookie, showError } from '@/utils';
import {
	Box,
	CircularProgress,
	Divider,
	Drawer,
	Stack,
	Typography
} from '@mui/material';
import { useMatch, useNavigate } from 'react-router-dom';
import routes from '@/routes';
import {
	usePaymentMethod,
	useShallowSelector,
	useSubscribed,
	useTrialDiscount
} from '@/hooks';
import { StampCheckmarkIcon } from '@/components/Icons/StampCheckmarkIcon';
import { useMedia } from '@/hooks/responsive';
import { CircleCheck } from '@/components/Icons';
import { fetchMe } from '@/store/auth/thunks';
import { TRIGGER_ACTIONS } from '@/store/editor';
import { PAYMENT_HANDLER, TRIAL_DAYS } from '@/config';
import { getPaymentModule } from '@/components/payment/getPaymentModule';
import { usePlans } from '@/hooks/price';
import StaticTermsDisclaimer from '@/components/StaticTermsDisclaimer';
import DialogHeader from '@/components/Dialog/DialogHeader';
import { fetchTransactions } from '@/store/editor/thunks';

import { TrialPlan } from './TrialPlan';
import { usePlanDescriptions, useStickyTop } from './hooks';
import { PAYMENT_BREAKDOWN_KEY } from '../PaymentBreakdownModal/constants';
import TrustPilotInfo from './TrustPilotInfo';
import EditedDocuments from './EditedDocuments';
import BannerBottom from './BannerBottom';
import { FastCheckoutImage } from './FastCheckoutImage';
import { TABS } from '../../my-account/constants';

const DISCOUNT_TIMER_MINUTES = 11;
const MINUTE_IN_MS = 60 * 1000;

const { PaymentElement } = getPaymentModule(PAYMENT_HANDLER);

export function FastCheckout(customProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const fastEditor = !!useMatch(routes.fastCheckout);
	const fastDashboard = !!useMatch(routes.fastCheckoutDashboard);
	const smDown = useMedia('smDown');
	const mdDown = useMedia('mdDown');
	const dialogRef = useStickyTop(smDown);
	const dispatch = useDispatch();
	const plansData = usePlans();

	const {
		trialPlan,
		loading: plansLoading,
		monthlyPlan,
		mainPlan
	} = plansData;

	const { currency, isModalFullDiscountOpen, user, priceCurrency } =
		useShallowSelector(state => ({
			currency: state.auth.currency,
			priceCurrency: state.auth.priceCurrency,
			isModalFullDiscountOpen: state.editor.isModalFullDiscountOpen,
			user: state.auth.user
		}));

	const isFastCheckoutRoute = fastEditor || fastDashboard;
	const isLessThanSevenDays = !!user && !hasPassedDays(user.createdAt, 7);
	const isOpen = !isModalFullDiscountOpen && isFastCheckoutRoute;
	const subscribed = useSubscribed();
	const trialDiscount = useTrialDiscount();

	const [previewLoading, setPreviewLoading] = useState(false);

	const handlePaymentSuccess = () => {
		if (user?.hasGoogleAdId) {
			setCookie(PAYMENT_BREAKDOWN_KEY, true);
		}

		setCookie(TRIGGER_ACTIONS.RATE_US, true);
	};

	const { hasPaymentMethod } = usePaymentMethod();

	const [initialRenderTime] = useState(dayjs());
	const [paymentReady, setPaymentReady] = useState(false);

	const trialPlanPrice = formatPrice({
		amount: trialPlan?.baseAmount,
		currency: currency || trialPlan?.currency
	});

	const planDescriptions = usePlanDescriptions(monthlyPlan);

	const loading = plansLoading || !paymentReady;

	useEffect(() => {
		if (isOpen) {
			(async function updateTransactions() {
				try {
					setPreviewLoading(true);
					dispatch(fetchTransactions());
				} catch (error) {
					showError(error);
				} finally {
					setPreviewLoading(false);
				}
			})();
		}
	}, [isOpen]);

	const loadingRender = (
		<Stack
			sx={{
				minHeight: 350,
				justifyContent: 'center',
				alignItems: 'center'
			}}
		>
			<CircularProgress />
		</Stack>
	);

	const previewRender = (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column'
			}}
		>
			<Box
				sx={{
					flexGrow: 1,
					p: { md: '20px' },
					pt: 0,
					paddingInlineEnd: { md: 0 },
					pb: { xs: 0, md: '10px' }
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: { xs: '100%', md: 467 },
						height: { xs: 177, md: '100%' },
						p: '20px',
						borderRadius: '8px',
						bgcolor: 'neutral.150',
						mb: { xs: '16px', md: 0 }
					}}
				>
					{previewLoading ? loadingRender : <FastCheckoutImage />}
				</Box>
			</Box>

			{!mdDown && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '16px',
						width: '100%',
						py: '16px',
						px: '12px',
						pr: 0,
						bgcolor: 'common.white'
					}}
				>
					<TrustPilotInfo />
					<EditedDocuments />
				</Box>
			)}
		</Box>
	);

	function handleClose(_, reason) {
		if (reason !== 'backdropClick') {
			if (
				isFastCheckoutRoute &&
				isLessThanSevenDays &&
				user?.hasGoogleAdId &&
				!subscribed &&
				!trialDiscount
			) {
				// ModalFullDiscount
			}

			navigate(fastDashboard ? routes.dashboard : routes.editor);
		}
	}

	useEffect(() => {
		const intervalId = setInterval(() => {
			const currentTime = dayjs();
			const startTime = dayjs(initialRenderTime);

			if (currentTime.diff(startTime, 'minutes') >= DISCOUNT_TIMER_MINUTES) {
				dispatch(fetchMe());
				clearInterval(intervalId);
			}
		}, MINUTE_IN_MS);

		return () => clearInterval(intervalId);
	}, [dispatch, initialRenderTime]);

	function getButtonText() {
		if (trialDiscount) {
			return t('fastCheckout.startTrialDiscount');
		}

		return t(`fastCheckout.button`);
	}

	const buttonText = getButtonText();

	const dialogHeader = (
		<DialogHeader
			title={t('fastCheckout.header')}
			iconComponent={<CircleCheck sx={{ color: '#05B37A' }} />}
			onClose={handleClose}
		/>
	);

	const content = (
		<>
			{loading && loadingRender}
			<Box
				sx={{
					'& form': { pr: 0 },
					...(loading
						? {
								maxHeight: 0,
								overflow: 'hidden',
								opacity: 0
							}
						: {})
				}}
			>
				<Box mb={3}>
					<Stack gap="12px" sx={{ pb: { xs: '12px', sm: '16px' } }}>
						<Typography
							variant="body3"
							sx={{
								fontWeight: 700,
								maxWidth: '95%',
								display: 'inline-block'
							}}
						>
							{t('fastCheckout.title', { trialDays: TRIAL_DAYS })}
						</Typography>

						<Stack gap="4px">
							{planDescriptions.map(({ i18nKey, Icon, transValues }) => (
								<Box
									key={i18nKey}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: '4px'
									}}
								>
									<Icon
										style={{
											width: '16px',
											height: '16px',
											color: 'text.primary',
											flexShrink: 0
										}}
									/>

									<Typography
										variant="body0"
										color="text.primary"
										sx={{ whiteSpace: 'pre-line' }}
									>
										<Trans
											i18nKey={i18nKey}
											values={transValues}
											components={{ strong: <strong /> }}
										/>
									</Typography>
								</Box>
							))}
						</Stack>
					</Stack>

					<Divider orientation="horizontal" />

					{trialPlan && !trialDiscount && !plansLoading && (
						<TrialPlan
							title={t('fastCheckout.trialTitle')}
							trialPlan={trialPlan}
							trialPlanPrice={trialPlanPrice}
							currency={currency}
							mainPlan={mainPlan}
						/>
					)}

					{trialDiscount && trialPlan && (
						<Stack
							alignItems="center"
							direction="row"
							justifyContent="space-between"
							pt={{ xs: '12px', md: '16px' }}
						>
							<Box alignItems="center" display="flex" gap="8px">
								<StampCheckmarkIcon
									sx={{
										backgroundColor: 'lime',
										borderRadius: '50%',
										fontSize: '32px',
										padding: '4px'
									}}
								/>

								<Typography
									variant={mdDown ? 'body2' : 'body3'}
									fontWeight={mdDown ? 500 : 400}
								>
									{t('fastCheckout.trialTitle')}
								</Typography>
							</Box>
							<Typography
								fontWeight={700}
								sx={{
									backgroundColor: 'lime',
									borderRadius: '4px',
									padding: '4px 8px',
									lineHeight: '28px',
									fontSize: { md: '24px' }
								}}
								variant="body3"
							>
								{formatPrice({
									amount: 0,
									currency: currency || trialPlan?.currency
								})}
							</Typography>
						</Stack>
					)}
				</Box>

				{!plansLoading && (
					<Box>
						<PaymentElement
							mode="checkout"
							hasPaymentMethod={hasPaymentMethod}
							priceId={trialPlan?.handlerId}
							mainPriceId={mainPlan?.handlerId}
							currency={currency || priceCurrency}
							onSuccess={() => {
								handlePaymentSuccess();

								const successRedirect = `${routes.myImages}?success=true`;

								navigate(successRedirect);
							}}
							onPaymentFormReady={payload => setPaymentReady(payload)}
							onError={error => {
								/* eslint-disable-next-line no-console */
								console.error(error);
								navigate(routes.myAccount, {
									state: { tab: TABS.BILLING }
								});
							}}
							checkoutMessage={buttonText}
							termsNode={
								<StaticTermsDisclaimer
									monthlyPlan={monthlyPlan}
									trialPlan={trialPlan}
									isTrialPlan
									buttonLabel={buttonText}
								/>
							}
							preload
						/>
					</Box>
				)}
			</Box>
		</>
	);

	if (smDown) {
		return (
			<Drawer
				open={isOpen}
				onClose={handleClose}
				anchor="bottom"
				sx={{
					'.MuiPaper-root': {
						borderRadius: '12px 12px 0px 0px',
						height: { xs: 'calc(100svh - 24px)', sm: 'auto' },
						maxHeight: 656,
						display: 'flex',
						flexDirection: 'column'
					}
				}}
			>
				<Box
					sx={{
						flexShrink: 0,
						zIndex: 1
					}}
				>
					{dialogHeader}
				</Box>

				<Box
					sx={{ px: 3, pt: '16px', pb: 3.5, flex: 1, overflowY: 'auto' }}
				>
					{mdDown && previewRender}
					{content}
				</Box>
			</Drawer>
		);
	}

	return (
		<Dialog
			ref={dialogRef}
			open={isOpen}
			onClose={handleClose}
			sx={{
				[`.${dialogClasses.paper}`]: {
					width: '100%',
					pt: 0,
					pb: { xs: 2, sm: 0 },
					px: 0,
					maxWidth: 1024,
					overflow: 'hidden'
				}
			}}
			maxWidth="xl"
			width="100%"
			hasCross={false}
			{...customProps}
		>
			{dialogHeader}

			<DialogContent
				sx={{ '&.MuiDialogContent-root': { p: 0, m: 0, pt: '24px' } }}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: {
							xs: 'column',
							md: 'row'
						},
						width: '100%',
						maxHeight: 'calc(100dvh - 193px)'
					}}
				>
					{!mdDown && previewRender}

					<Scrollbars
						className="dialog-scrollbars"
						hideTracksWhenNotNeeded
						autoHeight
						autoHeightMax={`calc(100dvh - ${198}px)`}
						renderTrackHorizontal={props => (
							<div
								{...props}
								style={{ display: 'none' }}
								className="track-horizontal"
							/>
						)}
					>
						<Box
							sx={{
								px: {
									xs: '8px',
									sm: '20px'
								},
								pb: '20px'
							}}
						>
							{mdDown && previewRender}

							{content}
						</Box>
					</Scrollbars>
				</Box>

				<BannerBottom />
			</DialogContent>
		</Dialog>
	);
}
